import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { ThemeToggle } from './ThemeToggle';
// Logo dosyalarını import etmiyoruz, çünkü tema bazlı değişim yapacağız
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProjectsOpen, setIsProjectsOpen] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Tema durumunu kontrol et
    const checkTheme = () => {
      const isDarkMode = document.documentElement.classList.contains('dark');
      setIsDark(isDarkMode);
    };

    // İlk yüklemede kontrol et
    checkTheme();

    // Tema değişikliklerini dinle
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  // Her render'da tema durumunu kontrol et
  useEffect(() => {
    const isDarkMode = document.documentElement.classList.contains('dark');
    setIsDark(isDarkMode);
  });

  const navigationItems = [
    { name: 'Ana Sayfa', path: '/' },
    { name: 'Hakkımızda', path: '/hakkimizda' },
    { name: 'Faaliyet Alanları', path: '/faaliyet-alanlari' },
    { name: 'İletişim', path: '/iletisim' },
  ];

  const projects = [
    { name: 'Frigolive', slug: 'frigolive' },
    { name: 'ShalterOn', slug: 'shalteron' },
    { name: 'Akıllı Geçiş Sistemi', slug: 'akilli-gecis-sistemi' },
    { name: 'Depo Takip Sistemi', slug: 'depo-takip-sistemi' },
    { name: 'Sanal Çapa Projesi', slug: 'sanal-capa-projesi' },
  ];

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <header className="bg-background border-b border-border sticky top-0 z-50 backdrop-blur-sm bg-background/95">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center">
              {isDark ? (
                <img 
                  src="/logo_beyaz_1.png" 
                  alt="İlimera Teknoloji" 
                  className="h-12 md:h-12 w-auto" 
                />
              ) : (
                <img 
                  src="/logo_siyah_2.png" 
                  alt="İlimera Teknoloji" 
                  className="h-12 md:h-12 w-auto" 
                />
              )}
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8 items-center">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-3 py-2 rounded-md transition-colors ${
                  isActive(item.path)
                    ? 'text-primary bg-green-50 dark:bg-green-900/20'
                    : 'text-gray-700 dark:text-gray-300 hover:text-primary hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                {item.name}
              </Link>
            ))}
            
            {/* Projects Dropdown - open only while hovering */}
            <DropdownMenu open={isProjectsOpen}>
              <div
                className="relative"
                onMouseEnter={() => setIsProjectsOpen(true)}
                onMouseLeave={() => setIsProjectsOpen(false)}
              >
                <DropdownMenuTrigger asChild>
                  <Link
                    to="/urunler"
                    className={`flex items-center px-3 py-2 rounded-md transition-colors ${
                      location.pathname.startsWith('/urunler')
                        ? 'text-primary bg-green-50 dark:bg-green-900/20'
                        : 'text-gray-700 dark:text-gray-300 hover:text-primary hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                  >
                    Ürünlerimiz
                    <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-200 ${isProjectsOpen ? 'rotate-180' : ''}`} />
                  </Link>
                </DropdownMenuTrigger>
                <div className="absolute left-0 top-full pt-2">
                  <DropdownMenuContent
                    align="start"
                    className={`w-56 origin-top-left rounded-xl shadow-2xl ring-1 ring-black/10 dark:ring-white/10 bg-white dark:bg-gray-900 text-gray-900 dark:text-white transform-gpu will-change-[opacity,transform] transition-all duration-300 ease-out ${
                      isProjectsOpen ? 'opacity-100 scale-100 translate-y-2' : 'opacity-0 scale-95 -translate-y-1 pointer-events-none'
                    }`}
                  >
                    <DropdownMenuItem asChild>
                      <Link to="/urunler/gelistirme-kartlari" className="w-full">
                        Geliştirme Kartları
                      </Link>
                    </DropdownMenuItem>
                    {projects
                      .filter((p) => p.slug !== 'sanal-capa-projesi')
                      .map((project) => (
                      <DropdownMenuItem key={project.slug} asChild>
                        <Link to={`/urunler/${project.slug}`} className="w-full">
                          {project.name}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </div>
              </div>
            </DropdownMenu>
          </nav>

          {/* Contact Button & Mobile Menu */}
          <div className="flex items-center space-x-2">
            <ThemeToggle />
            
            {/* Mobile menu button */}
            <button
              className="md:hidden ml-4 p-2 rounded-md text-gray-700 dark:text-gray-300 hover:text-primary hover:bg-gray-50 dark:hover:bg-gray-800"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-border">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-background">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block px-3 py-2 rounded-md transition-colors ${
                    isActive(item.path)
                      ? 'text-primary bg-green-50 dark:bg-green-900/20'
                      : 'text-gray-700 dark:text-gray-300 hover:text-primary hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Mobile Projects Menu */}
              <div className="space-y-1">
                <Link
                  to="/urunler"
                  className={`block px-3 py-2 rounded-md transition-colors ${
                    location.pathname === '/urunler'
                      ? 'text-primary bg-green-50 dark:bg-green-900/20'
                      : 'text-gray-700 dark:text-gray-300 hover:text-primary hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                  onClick={() => setIsMenuOpen(false)}
                >
                  Ürünlerimiz
                </Link>
                <div className="pl-4 space-y-1">
                  {/* Geliştirme Kartları sayfası */}
                  <Link
                    to="/urunler/gelistirme-kartlari"
                    className={`block px-3 py-1 text-sm rounded-md transition-colors ${
                      location.pathname === '/urunler/gelistirme-kartlari'
                        ? 'text-primary bg-green-50 dark:bg-green-900/20'
                        : 'text-gray-600 dark:text-gray-400 hover:text-primary hover:bg-gray-50 dark:hover:bg-gray-800'
                    }`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Geliştirme Kartları
                  </Link>

                  {/* Projeler - Sanal Çapa gizli */}
                  {projects
                    .filter((p) => p.slug !== 'sanal-capa-projesi')
                    .map((project) => (
                    <Link
                      key={project.slug}
                      to={`/urunler/${project.slug}`}
                      className={`block px-3 py-1 text-sm rounded-md transition-colors ${
                        location.pathname === `/urunler/${project.slug}`
                          ? 'text-primary bg-green-50 dark:bg-green-900/20'
                          : 'text-gray-600 dark:text-gray-400 hover:text-primary hover:bg-gray-50 dark:hover:bg-gray-800'
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {project.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}